<template>
  <div class="form-group" style="width: 100%;">
    <label v-if="label" :for="id">{{ label + (rules ? '*' : '') || id }}</label>
    <ValidationProvider :name="label || id" :rules="rules" v-slot="{ errors: providerErrors }">
      <div ref="multiselectWrapper" class="position-relative" style="min-width: 0;">
        <multiselect
          v-model="internalValue"
          :id="id"
          :options="computedOptions"
          :track-by="optionValueKey"
          :label="optionLabelKey"
          :multiple="multiple"
          :searchable="true"
          :allow-empty="allowEmpty"
          :deselect-label="allowEmpty ? 'Press enter to remove' : 'Selected value'"
          :close-on-select="!multiple"
          :clear-on-select="!multiple"
          :show-labels="true"
          :placeholder="placeholder"
          @blur="$emit('blur')"
          @open="checkDropdownPosition"
          :class="['form-control-sm', { 'is-invalid': hasErrors([...errors, ...providerErrors]) }, !allowEmpty ? 'no-deselect' : '', { 'dropdown-align-right': dropdownAlignRight }]"
        >
        </multiselect>
        <input
          :required="required"
          :value="internalRawValue"
          tabindex="-1"
          aria-hidden="true"
          class="invisible-required-input"
        />

        <div v-if="hasErrors([...errors, ...providerErrors])" class="invalid-feedback d-block">
          <div v-for="error in [...errors, ...providerErrors]" :key="error">{{ error }}</div>
        </div>
      </div>
    </ValidationProvider>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect';

export default {
  name: 'ValidatedSelect',
  components: {
    Multiselect,
  },
  props: {
    value: [String, Number, Object, Array],
    options: {
      type: Array,
      default: () => [],
    },
    id: {
      type: String,
      required: true,
    },
    label: String,
    rules: {
      type: [String, Object],
      default: '',
    },
    emptyOption: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    optionLabelKey: {
      type: String,
      default: 'label',
    },
    optionValueKey: {
      type: String,
      default: 'value',
    },
    placeholder: {
      type: String,
      default: 'Select option',
    },
    required: {
      type: Boolean,
      default: false,
    },
    allowEmpty: {
      type: Boolean,
      default() {
        return !this.required;
      },
    },
    errors: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      internalValue: this.multiple ? [] : null,
      dropdownAlignRight: false,
    };
  },
  mounted() {
    window.addEventListener('resize', this.checkDropdownPosition)
    window.addEventListener('scroll', this.checkDropdownPosition, true)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkDropdownPosition)
    window.removeEventListener('scroll', this.checkDropdownPosition, true)
  },
  computed: {
    computedOptions() {
      if (this.emptyOption && !this.required) {
        const empty = {
          [this.optionLabelKey]: 'None',
          [this.optionValueKey]: null,
        };
        return [empty, ...this.options];
      }
      return this.options;
    },
    internalRawValue() {
      if (this.multiple) {
        return Array.isArray(this.internalValue) && this.internalValue.length > 0 ? 'valid' : '';
      } else {
        return this.internalValue != null && this.internalValue !== '' ? 'valid' : '';
      }
    },
     allErrors() {
      return [...this.errors, ...this.providerErrors];
    }
  },
  watch: {
    value: {
      immediate: true,
      handler() {
        this.syncInternalValue();
      },
    },
    options: {
      immediate: true,
      deep: true,
      handler() {
        this.syncInternalValue();
      },
    },
    internalValue(newVal) {
      if (this.multiple) {
        const selectedValues = newVal.map((opt) => opt[this.optionValueKey]);
        this.$emit('input', selectedValues);
      } else {
        this.$emit('input', newVal ? newVal[this.optionValueKey] : null);
      }
    },
  },
  methods: {
    checkDropdownPosition() {
      if (!this.$refs.multiselectWrapper) return

      const wrapperEl = this.$refs.multiselectWrapper
      const rect = wrapperEl.getBoundingClientRect()
      const viewportWidth = window.innerWidth

      const dropdownWidth = rect.width + 180

      if (rect.left + dropdownWidth + 20 > viewportWidth) {
        this.dropdownAlignRight = true
      } else {
        this.dropdownAlignRight = false
      }
    },
    syncInternalValue() {
      if (!this.computedOptions) return;

      if (this.multiple) {
        this.internalValue = this.computedOptions.filter((opt) =>
          Array.isArray(this.value)
            ? this.value.includes(opt[this.optionValueKey])
            : false
        );
      } else {
        this.internalValue =
          this.computedOptions.find(
            (opt) => opt[this.optionValueKey] === this.value
          ) || null;
      }
    },
    hasErrors(allErrors) {
      return Array.isArray(allErrors) && allErrors.length > 0;
    },
    isSelected(option) {
      if (this.multiple) {
        return this.internalValue.some((opt) => opt[this.optionValueKey] === option[this.optionValueKey]);
      } else {
        return (
          this.internalValue &&
          this.internalValue[this.optionValueKey] === option[this.optionValueKey]
        );
      }
    },
  },
};
</script>

<style scoped>
::v-deep .multiselect.is-invalid {
  border: none !important;
  box-shadow: none !important;
}

::v-deep .multiselect {
  padding: 0;
  width: 100% !important;
  min-width: 0 !important;
  max-width: 100% !important;
  flex: 1 1 auto;
}

.invisible-required-input {
  opacity: 0;
  position: absolute;
  width: 0;
  height: 0;
  pointer-events: none;
}

::v-deep .multiselect__content-wrapper {
  position: absolute !important;
  min-width: 100% !important;
  width: calc(100% + 180px) !important;
  max-width: 500px;
  left: 0;
  right: auto;
}

::v-deep .multiselect.dropdown-align-right .multiselect__content-wrapper {
  left: auto;
  right: 0;
}

.form-group {
  width: 100%;
  min-width: 0;
  max-width: 100%;
}
::v-deep .multiselect__single {
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding-right: 2.5rem;
}

::v-deep .multiselect.no-deselect .multiselect__option--selected.multiselect__option--highlight {
  cursor: not-allowed !important;
  background: inherit !important;
  color: inherit !important;
}
</style>
